import { batchScalingEn, processRoutingEn } from './process-routing-en';

export const processRoutingEs = {
  ...processRoutingEn,
  title: 'Ruta de proceso',
  subtitle:
    'Instrucciones, controles de calidad, rendimiento y pérdidas versionados para esta versión exacta de receta.',
  statuses: {
    DRAFT: 'Borrador',
    IN_REVIEW: 'En revisión',
    APPROVED: 'Aprobada',
    BLOCKED: 'Bloqueada',
    ARCHIVED: 'Archivada',
  },
  help: {
    ...processRoutingEn.help,
    open: 'Abrir ayuda de rutas',
    header:
      'El número estable pertenece a la receta; cada versión de receta tiene su propia versión controlada de ruta.',
    translations: 'Los nombres e instrucciones pueden mantenerse en todos los idiomas admitidos.',
    steps:
      'Ordene los pasos mediante arrastrar y soltar. Los factores decimales se calculan en el servidor sin coma flotante.',
    history: 'Cada versión queda vinculada a su receta y es inmutable tras enviarla.',
    saveBeforeDocuments:
      'Guarde la ruta antes de cargar documentos y fotos en su expediente DMS protegido.',
    snapshotDocuments:
      'Los originales protegidos seleccionados forman la instantánea documental inmutable.',
  },
  actions: {
    reorder: 'Reordenar paso',
    scaleBatch: 'Escalar lote',
    submit: 'Enviar a revisión',
    review: 'Completar revisión',
    approve: 'Aprobar',
    copy: 'Nueva versión de ruta',
    block: 'Bloquear',
    archive: 'Archivar',
    addStep: 'Añadir paso',
    addByproduct: 'Añadir subproducto',
  },
  messages: {
    saved: 'Ruta guardada.',
    invalid: 'Corrija los datos o estructuras JSON no válidos.',
    submitted: 'Ruta enviada a revisión independiente.',
    workflowUpdated: 'Flujo de la ruta actualizado.',
  },
  sections: {
    header: 'Cabecera de ruta',
    translations: 'Idiomas',
    steps: 'Pasos de proceso',
    history: 'Historial de versiones',
    documents: 'Expediente DMS de la ruta',
    snapshotDocuments: 'Instantánea documental',
    stepTranslations: 'Instrucciones traducidas',
    byproducts: 'Subproductos',
    stepDocuments: 'Documentos y fotos del paso',
  },
  fields: {
    ...processRoutingEn.fields,
    number: 'Número de ruta',
    name: 'Nombre',
    processFactor: 'Factor total calculado',
    description: 'Descripción',
    localizedName: 'Nombre traducido',
    position: 'Posición',
    stepName: 'Nombre del paso',
    workplace: 'Puesto / área',
    input: 'Entrada',
    output: 'Salida',
    yield: 'Rendimiento %',
    loss: 'Pérdida %',
    version: 'Versión de ruta',
    recipeVersion: 'Versión de receta',
    status: 'Estado',
    updated: 'Último cambio',
    workCenter: 'Centro de trabajo',
    productionArea: 'Área de producción',
    inputQuantity: 'Cantidad de entrada',
    inputUnit: 'Unidad de entrada',
    outputQuantity: 'Cantidad de salida',
    outputUnit: 'Unidad de salida',
    targetTime: 'Tiempo objetivo (min)',
    setupTime: 'Preparación (min)',
    duration: 'Duración (min)',
    temperature: 'Temperatura',
    temperatureUnit: 'Unidad de temperatura',
    scrap: 'Merma %',
    absorption: 'Absorción %',
    instructions: 'Instrucciones de trabajo',
    parameters: 'Parámetros configurables (JSON)',
    qualityChecks: 'Controles de calidad (JSON)',
    product: 'Artículo',
    document: 'Documento',
    photo: 'Foto',
    reason: 'Motivo / nota trazable',
  },
  states: { noDocuments: 'No hay documentos de proceso protegidos.' },
  stepDialog: {
    title: 'Editar paso',
    description:
      'Mantenga cantidades, tiempos, parámetros, controles, pérdidas, instrucciones, subproductos y evidencias.',
  },
  actionDialog: {
    REVIEW: {
      title: 'Completar revisión técnica',
      description: 'Confirme la revisión independiente con una nota trazable.',
    },
    APPROVE: {
      title: 'Aprobar ruta',
      description:
        'La aprobación congela la ruta y exige receta aprobada y principio de cuatro ojos.',
    },
    COPY: {
      title: 'Crear nueva versión',
      description: 'Copia esta instantánea a un nuevo borrador con enlace de origen.',
    },
    BLOCK: {
      title: 'Bloquear ruta',
      description: 'La versión permanece inmutable, pero deja de ser utilizable.',
    },
    ARCHIVE: {
      title: 'Archivar ruta',
      description: 'La versión sigue siendo trazable y sale del uso activo.',
    },
  },
} as const;

export const batchScalingEs = {
  ...batchScalingEn,
  title: 'Escalado de lotes',
  subtitle: 'Cálculo de materiales reproducible en servidor en ambas direcciones.',
  help: {
    open: 'Abrir ayuda de escalado',
    input: 'Elija receta aprobada, dirección, unidad de salida y sustituciones aprobadas.',
    materials:
      'Las necesidades incluyen pérdidas y subrecetas y se redondean según la unidad objetivo.',
    formulas: 'Se muestra cada resultado intermedio y la fórmula exacta utilizada.',
  },
  sections: {
    input: 'Datos de cálculo',
    materials: 'Necesidades de material',
    limiting: 'Materiales limitantes',
    subRecipes: 'Subrecetas resueltas',
    byproducts: 'Subproductos esperados',
    formulas: 'Fórmulas y resultados intermedios',
    substitutions: 'Sustituciones aprobadas',
    available: 'Materiales disponibles',
  },
  fields: {
    recipe: 'Versión de receta',
    direction: 'Dirección de cálculo',
    outputUnit: 'Unidad de salida',
    position: 'Posición {{position}}',
    primaryComponent: 'Usar componente principal',
    targetQuantity: 'Cantidad final deseada',
    material: 'Material',
  },
  directions: {
    TARGET_OUTPUT: 'Cantidad final → materiales necesarios',
    AVAILABLE_INPUT: 'Material disponible → cantidad posible',
  },
  actions: { addMaterial: 'Añadir material', calculate: 'Calcular lote' },
  result: {
    output: 'Salida posible / deseada',
    scale: 'Escala de referencia',
    processFactor: 'Factor total',
    referenceOutput: 'Salida de referencia efectiva',
    level: 'Nivel BOM {{level}}',
  },
  columns: {
    article: 'Artículo',
    type: 'Tipo',
    quantity: 'Cantidad necesaria',
    source: 'Rutas de origen',
    available: 'Disponible',
    requiredReference: 'Necesario por referencia',
    possibleOutput: 'Salida posible',
  },
  componentTypes: { MATERIAL: 'Material', PACKAGING: 'Embalaje' },
  states: { none: 'Ninguno.' },
  messages: { invalid: 'Los datos de cálculo no son válidos.' },
  formulas: {
    PROCESS_STEP_FACTOR: 'Factor del paso',
    OVERALL_PROCESS_FACTOR: 'Factor total',
    ROOT_RECIPE_SCALE: 'Escala de receta principal',
    COMPONENT_GROSS_REQUIREMENT: 'Necesidad bruta del componente',
    SUB_RECIPE_SCALE: 'Escala de subreceta',
    AVAILABLE_MATERIAL_OUTPUT: 'Salida posible por material',
  },
  warnings: {
    PROCESS_ROUTING_MISSING_ASSUMED_100_PERCENT:
      'No hay ruta; se supone un rendimiento del 100 % que debe verificarse.',
    PROCESS_ROUTING_NOT_APPROVED: 'La ruta elegida no está aprobada.',
    STEP_YIELD_MISSING_ASSUMED_100_PERCENT:
      'Un paso no tiene rendimiento ni entrada/salida; se supone 100 %.',
    STEP_YIELD_CONTRADICTS_INPUT_OUTPUT:
      'El rendimiento indicado contradice la relación entrada/salida.',
    RECIPE_VERSION_NOT_APPROVED: 'Alguna versión de receta de la jerarquía no está aprobada.',
    COMPONENT_PRODUCT_BLOCKED_OR_ARCHIVED: 'Un artículo necesario está bloqueado o descatalogado.',
    MATERIAL_AVAILABILITY_NOT_PROVIDED:
      'No se indicó disponibilidad; este material no limita el resultado.',
    AVAILABLE_MATERIAL_NOT_REQUIRED: 'Un material indicado no es necesario.',
  },
} as const;
